import { Badge, Menu, rem } from "@mantine/core";
import IconPlus from "@src/assets/icons/lucide/plus.svg?react";
import IconTerminal from "@src/assets/icons/lucide/terminal.svg?react";
import IconEyeOff from "@src/assets/icons/tabler/eye-off.svg?react";
import { useBoardEvents } from "@src/components/board/board-events-context";
import { NavBarMenuButtonItem } from "@src/components/navigation/nav-bar-menu-button-item/nav-bar-menu-button-item";
import { NativeLikeTooltip as Tooltip } from "@src/components/ui/native-like-tooltip";
import clsx from "clsx";
import { useDesktopTerminalStore } from "./desktop-terminal-store";

function openTerminalDrawer(
	emit: ReturnType<typeof useBoardEvents>["emit"],
	id: string,
	title?: string,
) {
	useDesktopTerminalStore.getState().openTab(id, title);
	emit({
		type: "open-agent-chat-drawer",
		payload: {
			tab: "terminal",
		},
	});
}

export function TerminalsNavBarEntry({ compact }: { compact?: boolean } = {}) {
	const { tabs, hiddenCardTabs, createTab, activeTabId } =
		useDesktopTerminalStore();
	const { emit } = useBoardEvents();

	const activeCount = tabs.length;
	const hiddenCount = hiddenCardTabs.length;
	const totalCount = activeCount + hiddenCount;

	const handleSelectTerminal = (id: string, title?: string) => {
		openTerminalDrawer(emit, id, title);
	};

	const handleCreateNewTerminal = () => {
		createTab();
		emit({
			type: "open-agent-chat-drawer",
			payload: {
				tab: "terminal",
			},
		});
	};

	return (
		<Menu
			shadow="xl"
			radius="md"
			width={rem(260)}
			position="top-start"
			withinPortal
			zIndex={2000}
		>
			<Menu.Target>
				{compact ? (
					<Tooltip
						label={totalCount > 0 ? `Terminals (${totalCount})` : "Terminals"}
						position="top"
					>
						<button
							type="button"
							aria-label="Terminals"
							className={clsx(
								"relative flex shrink-0 items-center justify-center",
								"size-8 rounded-lg leading-none cursor-pointer",
								"text-text-secondary hover:text-text-primary hover:bg-surface-hover-subtle transition-colors",
							)}
						>
							<IconTerminal className="size-4 shrink-0" />
							{totalCount > 0 && (
								<span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#3fb950] ring-2 ring-surface-default" />
							)}
						</button>
					</Tooltip>
				) : (
					<div className="relative">
						<NavBarMenuButtonItem label="Terminals" icon={<IconTerminal />} />
						{totalCount > 0 && (
							<span className="absolute right-3 top-1/2 -translate-y-1/2 px-1.5 py-0.2 rounded-full text-[10px] font-mono font-bold bg-brand-primary/15 text-brand-primary">
								{totalCount}
							</span>
						)}
					</div>
				)}
			</Menu.Target>
			<Menu.Dropdown className="bg-surface-default border border-border-default text-text-primary">
				<Menu.Label className="text-text-muted font-mono text-[11px] uppercase tracking-wider">
					Active In Drawer ({activeCount})
				</Menu.Label>

				{tabs.length === 0 ? (
					<Menu.Item disabled className="text-xs text-text-muted">
						No open tabs
					</Menu.Item>
				) : (
					tabs.map((tab, idx) => {
						const isCard = tab.id.startsWith("card_");
						const isCurrent = tab.id === activeTabId;
						const title = tab.title || `Terminal ${idx + 1}`;

						return (
							<Menu.Item
								key={tab.id}
								onClick={() => handleSelectTerminal(tab.id, title)}
								className={clsx(
									"hover:bg-surface-hover-subtle text-xs font-mono py-1.5 transition-colors cursor-pointer text-text-primary",
									isCurrent &&
										"bg-surface-hover-muted font-semibold text-brand-primary",
								)}
								leftSection={
									<span
										className={clsx(
											"w-2 h-2 rounded-full shrink-0",
											tab.status === "ready" && "bg-[#3fb950]",
											tab.status === "connecting" &&
												"bg-[#d29922] animate-pulse",
											tab.status === "exited" && "bg-[#ff7b72]",
										)}
									/>
								}
								rightSection={
									isCard ? (
										<Badge size="xs" variant="outline" color="blue">
											Card
										</Badge>
									) : isCurrent ? (
										<Badge size="xs" variant="filled" color="blue">
											Active
										</Badge>
									) : null
								}
							>
								<span className="truncate max-w-35 block">{title}</span>
							</Menu.Item>
						);
					})
				)}

				{hiddenCardTabs.length > 0 && (
					<>
						<Menu.Divider className="border-border-subtle" />
						<Menu.Label className="text-text-muted font-mono text-[11px] uppercase tracking-wider">
							Hidden (Prompt Cards) ({hiddenCount})
						</Menu.Label>

						{hiddenCardTabs.map((tab) => {
							const title = tab.title || "Prompt Terminal";

							return (
								<Menu.Item
									key={tab.id}
									onClick={() => handleSelectTerminal(tab.id, title)}
									className="hover:bg-surface-hover-subtle text-xs font-mono py-1.5 transition-colors cursor-pointer text-text-secondary hover:text-text-primary"
									leftSection={
										<IconEyeOff className="w-3.5 h-3.5 shrink-0 text-text-muted" />
									}
									rightSection={
										<Badge size="xs" variant="subtle" color="gray">
											Hidden
										</Badge>
									}
								>
									<span className="truncate max-w-35 block">{title}</span>
								</Menu.Item>
							);
						})}
					</>
				)}

				<Menu.Divider className="border-border-subtle" />

				<Menu.Item
					onClick={handleCreateNewTerminal}
					className="hover:bg-surface-hover-subtle text-xs font-mono text-brand-primary font-medium py-1.5 cursor-pointer"
					leftSection={<IconPlus className="w-3.5 h-3.5 shrink-0" />}
				>
					New Terminal (⌘T)
				</Menu.Item>
			</Menu.Dropdown>
		</Menu>
	);
}

export function TerminalsMenuBarEntry() {
	const { tabs, hiddenCardTabs } = useDesktopTerminalStore();
	const { emit } = useBoardEvents();

	const totalCount = tabs.length + hiddenCardTabs.length;

	const handleOpen = () => {
		const targetId = tabs[0]?.id || "term_main";
		openTerminalDrawer(emit, targetId);
	};

	return (
		<Tooltip label={`Terminals (${totalCount})`}>
			<button
				type="button"
				aria-label="Open Terminals"
				title="Open Terminals"
				className={clsx(
					"relative inline-flex h-10.5 w-10.5 shrink-0",
					"items-center justify-center",
					"leading-none rounded-md border hover:shadow-md cursor-pointer",
					"text-brand-primary",
				)}
				onClick={handleOpen}
			>
				<IconTerminal width={20} height={20} strokeWidth={1.5} />
				{totalCount > 0 && (
					<span
						className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#3fb950] ring-2 ring-surface-default"
						aria-hidden="true"
					/>
				)}
			</button>
		</Tooltip>
	);
}
